import ButtonIcon from "components/ButtonIcon/ButtonIcon.tsx";
import Table from "components/Table/Table";
import Tooltip from "components/Tooltip/Tooltip";
import { commonColumnProps, PAGE_SIZE } from "constants/TableConstants";
import { useCallback, useState } from "react";
import { setShowClaimLinkingDrawer } from "reducers/drawer";
import { useGetClaimsQuery } from "services/claims-api";
import type { FileType } from "types/DataTypes";
import { useDispatch } from "utils/redux";

type LinkedClaimsListProps = {
  fileData: FileType;
  isReadOnly?: boolean;
};

const LinkedClaimList = ({
  fileData,
  isReadOnly = false,
}: LinkedClaimsListProps) => {
  const dispatch = useDispatch();
  const associatedClaimIds: number[] = fileData?.claim_associations?.map(
    (ass) => ass.claim,
  );
  const [page, setPage] = useState(1);

  // if we have no associated claims, use a dummy ID that won't match anything
  // this ensures we make the query below (for cache invalidation) but get no results
  const filters = {
    ids: associatedClaimIds?.length
      ? associatedClaimIds.map((id) => id.toString())
      : ["-1"],
  };

  const { data: claims, isLoading } = useGetClaimsQuery(
    { filters, searchTerm: undefined },
    {
      skip: false,
    },
  );

  const columns = [
    {
      key: "rre_rre_id",
      name: "RRE ID",
      ...commonColumnProps,
    },
    {
      key: "injured_party_first_name",
      name: "First Name",
      ...commonColumnProps,
    },
    {
      key: "injured_party_last_name",
      name: "Last Name",
      ...commonColumnProps,
    },
  ];

  const handleOpenSection111Link = useCallback(() => {
    if (fileData && fileData.section_111_claimant_url) {
      window.open(fileData.section_111_claimant_url);
    }
  }, [fileData]);

  return (
    <div className="special-client-instructions card form">
      <div className="card__header">
        Linked Claims
        {!isReadOnly && (
          <div className="flex jc-end">
            <Tooltip
              content="Manage linked claims"
              place="right"
              variant="info"
            >
              <ButtonIcon
                kind="neutral"
                className="header-action"
                onClick={(e) => {
                  e?.preventDefault();
                  dispatch(setShowClaimLinkingDrawer(true));
                }}
              >
                add_link
              </ButtonIcon>
            </Tooltip>
            <Tooltip
              content="Open claims in Section 111"
              place="left"
              variant="info"
            >
              <ButtonIcon
                kind="neutral"
                className="header-action"
                onClick={(e) => {
                  e?.preventDefault();
                  handleOpenSection111Link();
                }}
              >
                view_stream
              </ButtonIcon>
            </Tooltip>
          </div>
        )}
      </div>

      <Table
        className="table client-instructions-table"
        columns={columns}
        rows={claims?.results || []}
        rowKeyGetter={(row: { id: number }) => row.id}
        emptyTableMessage="No linked claims"
        variant="compact"
        pagination={{
          pageSize: PAGE_SIZE,
          pageIndex: page,
          rowCount: claims?.count || 0,
          onPageChange: setPage,
          isLoading,
        }}
      />
    </div>
  );
};

export default LinkedClaimList;
